import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Box,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Paper
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../api';

function MultipleChoiceQuiz({ vehicleId }) {
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [score, setScore] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [userAnswers, setUserAnswers] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [vehicleResponse, equipmentResponse] = await Promise.all([
          api.get(`/vehicles/${vehicleId}`),
          api.get(`/equipment`)
        ]);
        setVehicle(vehicleResponse.data);
        setEquipment(equipmentResponse.data);
        setLoading(false);
      } catch (error) {
        console.error('Fehler beim Laden der Daten:', error);
        setLoading(false);
      }
    };

    if (vehicleId) {
      fetchData();
    }
  }, [vehicleId]);

  const generateQuestions = useCallback(() => {
    const newQuestions = [];
    const compartments = vehicle.compartments;

    // Fragen über Gerätefächer und ihre Ausrüstung
    compartments.forEach(compartment => {
      const compartmentEquipment = compartment.equipment.map(id => 
        equipment.find(e => e.id === id)
      ).filter(Boolean);

      if (compartmentEquipment.length > 0) {
        newQuestions.push({
          question: `Welche Ausrüstung gehört zum Bereich ${compartment.name} des ${vehicle.name}?`,
          correctAnswer: compartmentEquipment[0].name,
          options: [
            compartmentEquipment[0].name,
            ...equipment
              .filter(e => !compartmentEquipment.includes(e))
              .sort(() => Math.random() - 0.5)
              .slice(0, 3)
              .map(e => e.name)
          ].sort(() => Math.random() - 0.5)
        });
      }
    });

    // Fülle auf 15 Fragen auf
    while (newQuestions.length < 15) {
      const randomCompartment = compartments[Math.floor(Math.random() * compartments.length)];
      const randomEquipment = equipment[Math.floor(Math.random() * equipment.length)];
      
      newQuestions.push({
        question: `Befindet sich ${randomEquipment.name} in ${randomCompartment.name} des ${vehicle.name}?`,
        correctAnswer: randomCompartment.equipment.includes(randomEquipment.id) ? 'Ja' : 'Nein',
        options: ['Ja', 'Nein']
      });
    }

    setQuestions(newQuestions);
  }, [vehicle, equipment]);

  useEffect(() => {
    if (vehicle && equipment.length > 0) {
      generateQuestions();
    }
  }, [vehicle, equipment, generateQuestions]);

  const handleAnswerSubmit = async () => {
    const currentQuestion = questions[currentQuestionIndex];
    const isCorrect = selectedAnswer === currentQuestion.correctAnswer;
    
    setUserAnswers(prev => [...prev, {
      question: currentQuestion.question,
      userAnswer: selectedAnswer,
      correctAnswer: currentQuestion.correctAnswer,
      isCorrect
    }]);

    if (isCorrect) {
      setScore(score + 1);
    }

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer('');
    } else {
      setShowResults(true);
      
      // Highscore-Funktion entfernt
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!vehicle || !equipment.length || !questions.length) {
    return (
      <Container>
        <Typography variant="h5" color="error">
          Daten werden geladen...
        </Typography>
        <Button onClick={() => navigate('/')}>Zurück zur Auswahl</Button>
      </Container>
    );
  }

  if (showResults) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom align="center">
            Quiz abgeschlossen!
          </Typography>
          <Typography variant="h5" gutterBottom align="center">
            Punktzahl: {score} von {questions.length}
          </Typography>
          <Box sx={{ mt: 4 }}>
            {userAnswers.map((answer, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Frage {index + 1}: {answer.question}
                </Typography>
                <Typography
                  variant="body1"
                  color={answer.isCorrect ? 'success.main' : 'error.main'}
                >
                  Deine Antwort: {answer.userAnswer}
                </Typography>
                {!answer.isCorrect && (
                  <Typography variant="body1" color="success.main">
                    Richtige Antwort: {answer.correctAnswer}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/')}
            fullWidth
            sx={{ mt: 2 }}
          >
            Zurück zur Auswahl
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/')}
        sx={{ mb: 2 }}
      >
        Zurück zur Auswahl
      </Button>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Multiple-Choice-Quiz: {vehicle.name}
        </Typography>
        <Typography variant="h6" gutterBottom>
          Frage {currentQuestionIndex + 1} von {questions.length}
        </Typography>
        <Typography variant="h5" gutterBottom>
          {questions[currentQuestionIndex].question}
        </Typography>
        <FormControl component="fieldset" sx={{ mt: 2 }}>
          <RadioGroup
            value={selectedAnswer}
            onChange={(e) => setSelectedAnswer(e.target.value)}
          >
            {questions[currentQuestionIndex].options.map((option, index) => (
              <FormControlLabel
                key={index}
                value={option}
                control={<Radio />}
                label={option}
              />
            ))}
          </RadioGroup>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAnswerSubmit}
          disabled={!selectedAnswer}
          fullWidth
          sx={{ mt: 2 }}
        >
          {currentQuestionIndex < questions.length - 1 ? 'Nächste Frage' : 'Quiz beenden'}
        </Button>
      </Paper>
    </Container>
  );
}

export default MultipleChoiceQuiz; 
